import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  signal,
  OnInit,
  DestroyRef,
} from '@angular/core';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { ApiV1_Establishment } from '@pedix-workspace/api-v1';
import {
  EstablishmentOpenedStatus,
  getEstablishmentTimezone,
} from '@pedix-workspace/utils';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { TagComponent } from '@pedix-workspace/angular-ui-alert';
import { switchMap } from 'rxjs';
import { ModalDialogService } from '@pedix-workspace/angular-ui-modal';
import { WorkingHoursService } from '@pedix-workspace/pedixapp-core-services';
import { WorkingHoursMessageDialogComponent } from '../working-hours-message-dialog/working-hours-message-dialog.component';

@Component({
  selector: 'pxw-establishment-opened-status-tag',
  templateUrl: './establishment-opened-status-tag.component.html',
  styleUrls: ['./establishment-opened-status-tag.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslocoDirective, TagComponent],
  standalone: true,
})
export class EstablishmentOpenedStatusTagComponent implements OnInit {
  private modalDialogService = inject(ModalDialogService);
  private t = inject(TranslocoService);
  private workingHoursService = inject(WorkingHoursService);
  private destroyRef = inject(DestroyRef);

  readonly establishment =
    input.required<Pick<ApiV1_Establishment, 'workingDays' | 'temporaryClosure' | 'addressDetails' | 'countryCode'>>();
  readonly establishment$ = toObservable(this.establishment);

  readonly openedStatus = signal<EstablishmentOpenedStatus | null>(null);

  readonly workingDays = computed(() => this.establishment().workingDays);
  readonly temporaryClosure = computed(() => this.establishment().temporaryClosure);
  readonly tagType = computed(() => {
    const openedStatus = this.openedStatus();

    if (!openedStatus) {
      return undefined;
    }

    if (openedStatus.isTemporaryClosed || openedStatus.isClosed) {
      return 'danger';
    }
    if (openedStatus.isAboutToClose) {
      return 'warning';
    }
    return 'success';
  });
  readonly tagText = computed(() => {
    const openedStatus = this.openedStatus();

    if (!openedStatus) {
      return undefined;
    }

    if (openedStatus.isTemporaryClosed || openedStatus.isClosed) {
      return this.t.translate('workingHours.isClosed');
    }
    if (openedStatus.isAboutToClose) {
      return this.t.translate('workingHours.isAboutToClose');
    }
    return this.t.translate('workingHours.isOpened');
  });

  ngOnInit() {
    this.establishment$
      .pipe(
        switchMap(establishment =>
          this.workingHoursService.isEstablishmentOpened$(
            establishment.workingDays,
            establishment.temporaryClosure,
            {
              timezone: getEstablishmentTimezone(establishment),
            },
          ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(openedStatus => {
        this.openedStatus.set(openedStatus);
      });
  }

  displayWorkingHours(event: Event) {
    event.stopPropagation();

    this.modalDialogService
      .attachDialog(WorkingHoursMessageDialogComponent)
      .open(this.workingDays() ?? null);
  }
}
